import { FC, FormEventHandler, Fragment, useReducer, useState } from 'react';

import { RefreshCcw } from 'lucide-react';
import { values } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import css from './ResetMarksButton.module.css';

import { slotService } from 'v2/api/slotService';
import { CheckBoxAutocomplete } from 'v2/components/autocomplete/checkBoxAutocomplete/CheckBoxAutocomplete';
import { SlotMark } from 'v2/constants';
import { handleHttpError } from 'v2/utils';

interface ResetMarksButtonProps {
  onSuccess: VoidFunction;
}

export const ResetMarksButton: FC<ResetMarksButtonProps> = ({ onSuccess }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const [marks, setMarks] = useState<SlotMark[]>([]);

  const { trigger, isMutating } = useSWRMutation('resetSlotsMarks', slotService.resetSlotsMarks, {
    onSuccess: () => {
      onSuccess();
      toast.success('The slot marks have been reset');
      handleClose();
    },
    onError: handleHttpError,
  });

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();

    trigger(marks);
  };

  const handleClose = () => {
    setOpen();
    setMarks([]);
  };

  return (
    <Fragment>
      <Button onClick={setOpen} startIcon={<RefreshCcw width={20} height={20} />} variant="outlined" size="large">
        Reset Marks
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Marks</DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent className={css.resetMarksForm}>
            <CheckBoxAutocomplete label="Marks" value={marks} options={values(SlotMark)} onChange={setMarks} />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} color="error" variant="outlined">
              Cancel
            </Button>

            <Button type="submit" variant="contained" loading={isMutating}>
              Reset
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Fragment>
  );
};
